// Typed re-export of the pure token grammar in `resolveTemplate.mjs`. The TypeScript
// callers import from here; the node tests import the .mjs module directly.

import type { ControllerKind } from "../Processing/types";
import {
  BRACE_RE,
  CURRENT_VARIABLES,
  DATE_VARIABLES,
  DEFAULT_DATE_FORMAT,
  LIST_VARIABLES,
  SLICER_VARIABLES as SLICER_VARIABLES_RAW,
  SLICER_VARIABLE_ORDER as SLICER_VARIABLE_ORDER_RAW,
  TOKEN_RE,
  canonicalVariable as canonicalVariableRaw,
  kindWord as kindWordRaw,
  parseTokens as parseTokensRaw,
  resolveTemplate as resolveTemplateRaw,
  saveBlockingWarnings as saveBlockingWarningsRaw,
  slicerKeysInTemplate as slicerKeysInTemplateRaw,
  variablesForControls as variablesForControlsRaw,
} from "./resolveTemplate.mjs";

export { BRACE_RE, CURRENT_VARIABLES, DATE_VARIABLES, DEFAULT_DATE_FORMAT, LIST_VARIABLES, TOKEN_RE };

export type SlicerKind = "date" | "subject";

export type SlicerVariable =
  | "StartDate"
  | "EndDate"
  | "DateRange"
  | "DayFilter"
  | "Subjects"
  | "SubjectCount"
  | "Athlete"
  | "Drills";

/** One slicer's client values: every variable its kind offers, null when not available yet. */
export interface SlicerValueEntry {
  kind: SlicerKind;
  label: string;
  values: Partial<Record<SlicerVariable, string | null>>;
}

/** The slicer value map, keyed by the upper-cased slicer key. */
export type SlicerValues = Record<string, SlicerValueEntry>;

export interface ResolveResult {
  resolved: string;
  warnings: string[];
}

export interface ResolveOptions {
  /** The clock for the `{CurrentDate}` family; defaults to now. */
  now?: Date;
  /** Escape every substituted value; on for text content, off for a title. */
  escapeHtml?: boolean;
}

export interface Token {
  raw: string;
  start: number;
  end: number;
  prefixed: boolean;
  /** Upper-cased slicer key; null for an unprefixed token. */
  key: string | null;
  /** Canonical spelling for a known slicer variable, as typed otherwise. */
  variable: string;
  sign: "+" | "-" | null;
  amount: number | null;
  format: string | null;
}

export const SLICER_VARIABLES = SLICER_VARIABLES_RAW as Record<SlicerVariable, readonly ControllerKind[]>;

export const SLICER_VARIABLE_ORDER = SLICER_VARIABLE_ORDER_RAW as Record<ControllerKind, readonly SlicerVariable[]>;

export const canonicalVariable = canonicalVariableRaw as (name: string) => SlicerVariable | null;

export const variablesForControls: (controls: readonly ControllerKind[] | undefined) => SlicerVariable[] =
  variablesForControlsRaw;

export const kindWord: (controls: readonly ControllerKind[] | undefined) => SlicerKind = kindWordRaw;

export const parseTokens: (text: string) => Token[] = parseTokensRaw;

export const slicerKeysInTemplate: (text: string) => Set<string> = slicerKeysInTemplateRaw;

export const resolveTemplate: (
  template: string,
  values?: SlicerValues,
  options?: ResolveOptions,
) => ResolveResult = resolveTemplateRaw;

export const saveBlockingWarnings: (template: string, values?: SlicerValues) => string[] =
  saveBlockingWarningsRaw;
